import React from 'react';
import { Accordion, FieldErrors, Icon } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Select from './Select';
import useAppContext from '../../../../../context/useAppContext';
import { getInventoryName } from '../utils';
import css from '../../styles.module.css';

export default function RatesSelect({
    form,
    onOpen = () => {},
    rates,
    quotas,
    inventories,
    setRates,
    bookingSelectionAuto,
    onChangeSelectedRate,
    onUnselectRates,
    ratesTree,
    loadingRates,
    isInvitations
}) {
    let [{ organization, event }] = useAppContext();
    const { t } = useTranslation();
    const selectorLabel = t('attendees.addAttendees.addTarif');
    const selectProps = { organization, event, selectorLabel, onOpen };

    const getSelectedRates = React.useCallback(
        ({ session = null, inventory = null }) => {
            let selected_rates = [...form.selected_rates];
            if (session) {
                selected_rates = form.selected_rates.filter(s_rate => s_rate.rate.sessionId === session.value);
            }

            if (inventory) {
                selected_rates = form.selected_rates.filter(s_rate => s_rate.inventory.pk === inventory.pk);
            }
            return selected_rates;
        },
        [form, form.selected_rates]
    );

    const renderSessions = React.useMemo(() => {
        return form.sessions.map(session => {
            const session_remaining = quotas[event.id][session.weez_date_id]['available'] - quotas[event.id][session.weez_date_id]['taken'];

            const remaining_quota = session_remaining > 0 ? session_remaining : 0;
            const has_quotas = quotas[event.id][session.weez_date_id]['available'] < Infinity;
            let rates_sessions = rates.filter(rate => rate.sessionId && rate.sessionId === session.value);

            return (
                <Accordion
                    key={session.id}
                    disableToggle={true}
                    openByDefault
                    title={
                        <span className={css['session-manager-title']}>
                            <div className={css['booking-rates-title']}>{session.text}</div>
                            {has_quotas && form.selected_rates.length > 0 && (
                                <div className={css['rate-subtitle']}>{t('attendee.order.session_quota', { quota: remaining_quota })}</div>
                            )}
                        </span>
                    }
                >
                    <Select
                        {...selectProps}
                        form={form}
                        rates={rates_sessions}
                        tree={ratesTree}
                        loading={loadingRates}
                        remaining_quota={remaining_quota}
                        onSelect={selected => setRates(selected, { session: session })}
                        onUnselectRates={onUnselectRates}
                        session={session}
                        setRates={setRates}
                        selected_rates={getSelectedRates({ session })}
                        onChangeSelectedRate={onChangeSelectedRate}
                        isInvitations={isInvitations}
                    />
                    {session_remaining < 0 && (
                        <div className={css['quotas-details-container']}>
                            <FieldErrors
                                className={css['rates-manager-quotas-warning']}
                                errors={[<Icon error name="message-error-inverted" label="test" color="target" />, t('attendee.order.quotaSessionOver')]} // eslint-disable-line
                            />
                        </div>
                    )}
                </Accordion>
            );
        });
    }, [form, quotas, rates, ratesTree, loadingRates, onUnselectRates, getSelectedRates, onChangeSelectedRate]);

    if (form.use_sessions && event.isUseSessionsNoSeated()) {
        return renderSessions;
    }

    if (form.inventories?.length) {
        return form.inventories.map(inventory => {
            let actualInventory = inventories.find(inv => inv.uuid === inventory.pk);
            let rates_inventories = rates.filter(rate => rate.inventories && rate.inventories.includes(actualInventory.uuid));
            return (
                <Accordion key={inventory.pk} disableToggle={true} openByDefault title={getInventoryName(event.isFakeSeating(), inventory, event.timezone)}>
                    <Select
                        {...selectProps}
                        key={inventory.pk}
                        inventory={actualInventory}
                        form={form}
                        loading={loadingRates}
                        tree={ratesTree}
                        rates={rates_inventories}
                        bookingSelectionAuto={bookingSelectionAuto}
                        onSelect={selected => setRates(selected, { inventory: inventory })}
                        onUnselectRates={onUnselectRates}
                        onChangeSelectedRate={onChangeSelectedRate}
                        selected_rates={getSelectedRates({ inventory })}
                        isInvitations={isInvitations}
                    />
                </Accordion>
            );
        });
    }

    return (
        <Select
            {...selectProps}
            rates={rates}
            form={form}
            tree={ratesTree}
            loading={loadingRates}
            bookingSelectionAuto={bookingSelectionAuto}
            onSelect={selected => setRates(selected)}
            selected_rates={form.selected_rates}
            onUnselectRates={onUnselectRates}
            onChangeSelectedRate={onChangeSelectedRate}
            isInvitations={isInvitations}
        />
    );
}
